/**
 * Core types for LaTeX autocomplete system
 */

export enum CommandCategory {
  STRUCTURE = 'structure',      // \section, \begin, etc.
  MATH = 'math',                // \frac, \sum, etc.
  TEXT = 'text',                // \textbf, \emph, etc.
  CITATION = 'citation',        // \cite, \ref, etc.
  ENVIRONMENT = 'environment',  // \begin, \end
  PACKAGE = 'package',          // \usepackage, \documentclass
  CUSTOM = 'custom'
}

export enum EnvironmentCategory {
  MATH = 'math',              // equation, align, etc.
  LIST = 'list',              // itemize, enumerate
  TABLE = 'table',            // table, tabular
  FIGURE = 'figure',          // figure
  TEXT = 'text',              // quote, verse
  CUSTOM = 'custom'
}

export enum ContextMode {
  NORMAL = 'normal',
  MATH_INLINE = 'math_inline',
  MATH_DISPLAY = 'math_display',
  VERBATIM = 'verbatim',
  COMMENT = 'comment'
}

export enum CompletionStage {
  COMMAND_SELECTION = 'command_selection',     // Selecting \begin
  ARGUMENT_INPUT = 'argument_input',          // Typing in \begin{}
  ENVIRONMENT_SELECTION = 'environment_selection', // Selecting environment name
  SNIPPET_NAVIGATION = 'snippet_navigation'    // Tab through snippet placeholders
}

export interface ArgumentSpec {
  type: 'required' | 'optional' | 'starred'
  placeholder?: string
  suggestions?: string[] | 'environments' | 'packages' | 'citations' // For autocomplete within argument
}

export interface CommandMetadata {
  name: string
  category: CommandCategory
  arguments: ArgumentSpec[]
  snippet?: string
  description: string
  usageFrequency: number // For ranking (0-100)
  requiresMathMode?: boolean
  requiresPackage?: string[]
  aliases?: string[]
}

export interface EnvironmentMetadata {
  name: string
  category: EnvironmentCategory
  description: string
  requiresPackage?: string[]
  commonOptions?: string[] // For \begin{env}[options]
  innerCommands?: string[] // Commands commonly used inside
}

export interface LaTeXContext {
  mode: ContextMode
  environment?: string              // Current environment name
  depth: number                      // Nesting depth
  inMathMode: boolean                 // $...$ or \(...\)
  inDisplayMath: boolean             // \[...\]
  recentCommands: string[]           // Last 10 commands
  packages: string[]                  // Loaded packages
  position: number                    // Cursor position
}

export interface PositionInfo {
  isInCommand: boolean
  commandName?: string
  isInArgument: boolean
  argumentIndex?: number
  isInEnvironment: boolean
  environmentName?: string
  isInBraces: boolean
  braceDepth: number
  isAfterBackslash: boolean
  textBeforeCursor: string
  textAfterCursor: string
}

export interface TabStop {
  index: number
  start: number
  end: number
  placeholder?: string
  isFinal: boolean
}

export interface ParsedSnippet {
  text: string
  tabStops: TabStop[]
  finalPosition: number
}

export interface CompletionState {
  stage: CompletionStage
  command?: string
  insertedText?: string
  cursorPosition?: number
  snippetTabStops?: TabStop[]
  currentTabStop?: number
}

